/**
 * Custom hook for LeadsV2 page - manages all state and API calls
 * Supports URL-based state management for navigation preservation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useModal } from '../../../contexts/ModalContext';

const useLeadsV2 = (dateRangeState = null, visibleColumns = []) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openNoChangesModal, openErrorModal } = useModal();
  
  // Parse URL params to initialize state
  const getInitialState = () => {
    return {
      // Pagination
      page: parseInt(searchParams.get('page')) || 1,
      pageSize: parseInt(searchParams.get('page_size')) || 100,
      
      sortField: searchParams.get('sort_field') || 'updated_at',
      sortOrder: searchParams.get('sort_order') || 'desc',
      
      // Custom Fetch Mode (from dashboard)
      statType: searchParams.get('stat_type') || null,
      startDate: searchParams.get('start_date') || null,
      endDate: searchParams.get('end_date') || null,
      assignedTo: [],
      notInterestedReasonCategory: searchParams.get('not_interested_reason_category') || null,

      // Filters — start empty; do not hydrate from URL so page load doesn't auto-apply filters
      isDeleted: false,
      filters: {},

      // Pipeline filters — stage/status set by sidebar (ANDed with filters in payload)
      pipelineFilters: {},

      // Sidebar selection — do not hydrate from URL either
      sidebarStatus: null,
    };
  };
  
  const [state, setState] = useState(getInitialState());
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 50,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });
  
  // Ref to track current request
  const currentRequestRef = useRef(null);

  // Always-fresh state ref so fetchLeads never reads stale closures
  const stateRef = useRef(state);
  stateRef.current = state;
  
  // Track previous date range to detect changes
  const prevDateRangeRef = useRef(null);
  const isInitialMountRef = useRef(true);
  
  // Update URL when state changes
  const updateURL = useCallback((newState) => {
    const params = new URLSearchParams();
    
    // Add core state to URL (but do NOT encode filters/agents to avoid auto-applying on reload)
    if (newState.page > 1) params.set('page', newState.page);
    if (newState.sortField !== 'updated_at') params.set('sort_field', newState.sortField);
    if (newState.sortOrder !== 'desc') params.set('sort_order', newState.sortOrder);
    
    // Custom fetch params
    if (newState.statType) params.set('stat_type', newState.statType);
    if (newState.startDate) params.set('start_date', newState.startDate);
    if (newState.endDate) params.set('end_date', newState.endDate);
    if (newState.notInterestedReasonCategory) params.set('not_interested_reason_category', newState.notInterestedReasonCategory);
    
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Fetch leads from API — reads stateRef so the callback identity is stable
  // and the effect that calls it always gets the latest state.
  const fetchLeads = useCallback(async (stateOverride = {}) => {
    const currentState = { ...stateRef.current, ...stateOverride };
    
    setIsLoading(true);
    setError(null);
    
    // Generate request ID
    const requestId = Date.now();
    currentRequestRef.current = requestId;
    
    try {
      const payload = buildAPIPayload(currentState, dateRangeState, visibleColumns);
      
      const response = await api.post('/lms/leads/v2/', payload);
      
      // Check if this is still the current request
      if (currentRequestRef.current !== requestId) {
        return;
      }
      
      if (response.data && response.data.success) {
        setLeads(response.data.leads || []);
        setPaginationInfo(response.data.pagination || {});
      } else {
        setError('Failed to fetch leads');
        setLeads([]);
      }
    } catch (err) {
      if (currentRequestRef.current !== requestId) {
        return;
      }
      
      console.error('Error fetching leads:', err);
      setError(err.response?.data?.message || 'Failed to fetch leads');
      setLeads([]);
    } finally {
      if (currentRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [dateRangeState, visibleColumns]);
  
  // Update state and URL, then fetch
  // Do not call setSearchParams inside the setState updater — it updates Router and triggers
  // "Cannot update a component (BrowserRouter) while rendering a different component (LeadsV2)".
  const updateState = useCallback((updates) => {
    setState(prev => {
      const newState = { ...prev, ...updates };
      queueMicrotask(() => updateURL(newState));
      return newState;
    });
  }, [updateURL]);
  
  // Reset page when date range changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevDateRangeRef.current = {
        filterId: dateRangeState?.filter?.id,
        startDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
        endDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD'),
        allResults: dateRangeState?.all_results,
      };
      return;
    }

    const currentDateRange = {
      filterId: dateRangeState?.filter?.id,
      startDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
      endDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD'),
      allResults: dateRangeState?.all_results,
    };

    // Check if date range actually changed
    const dateRangeChanged = 
      prevDateRangeRef.current?.filterId !== currentDateRange.filterId ||
      prevDateRangeRef.current?.startDate !== currentDateRange.startDate ||
      prevDateRangeRef.current?.endDate !== currentDateRange.endDate ||
      prevDateRangeRef.current?.allResults !== currentDateRange.allResults;

    if (dateRangeChanged) {
      setState(prev => {
        const newState = {
          ...prev,
          page: 1,
          filters: {},
          pipelineFilters: {},
          isDeleted: false,
          sidebarStatus: null,
        };
        queueMicrotask(() => updateURL(newState));
        return newState;
      });
    }

    prevDateRangeRef.current = currentDateRange;
  }, [
    dateRangeState?.filter?.id,
    dateRangeState?.currentStart?.format('YYYY-MM-DD'),
    dateRangeState?.currentEnd?.format('YYYY-MM-DD'),
    dateRangeState?.all_results,
    updateURL,
  ]);

  // Fetch leads when state changes
  useEffect(() => {
    fetchLeads();
  }, [
    state.page,
    state.pageSize,
    state.sortField,
    state.sortOrder,
    state.statType,
    state.startDate,
    state.endDate,
    state.isDeleted,
    state.notInterestedReasonCategory,
    JSON.stringify(state.assignedTo),
    JSON.stringify(state.filters),
    JSON.stringify(state.pipelineFilters),
    dateRangeState?.filter?.id,
    dateRangeState?.currentStart?.format('YYYY-MM-DD'),
    dateRangeState?.currentEnd?.format('YYYY-MM-DD'),
    dateRangeState?.all_results,
  ]);
  
  // Action handlers
  const handlePageChange = useCallback((newPage) => {
    updateState({ page: newPage });
  }, [updateState]);
  
  const handlePageSizeChange = useCallback((newSize) => {
    updateState({ page: 1, pageSize: newSize });
  }, [updateState]);
  
  const handleSort = useCallback((field) => {
    // Calculate new sort order based on current state
    const newSortOrder = state.sortField === field && state.sortOrder === 'asc' ? 'desc' : 'asc';
    updateState({
      page: 1,
      sortField: field,
      sortOrder: newSortOrder,
    });
  }, [updateState, state.sortField, state.sortOrder]);
  
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const normalizedFilters = newFilters || {};
      const isCleared = Object.keys(normalizedFilters).length === 0;

      updateState({
        page: 1,
        filters: normalizedFilters,
        ...(isCleared
          ? {
              // When filters are fully cleared (e.g. Clear in LeadFilters),
              // reset pipeline/sidebar selection back to "all"
              isDeleted: false,
              sidebarStatus: null,
              pipelineFilters: {},
            }
          : {}),
      });
    },
    [updateState],
  );
  
  const handleRefresh = useCallback(() => {
    fetchLeads();
  }, [fetchLeads]);
  
  // Export function - triggers email export (CSV columns = table visible_columns only; no server defaults)
  const handleExport = useCallback(async () => {
    if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) {
      openErrorModal(
        'Cannot export',
        'Choose at least one column in Customize columns before exporting.'
      );
      return;
    }
    try {
      console.log('Starting export...');
      const payload = buildAPIPayload({ ...stateRef.current, is_export: true }, dateRangeState, visibleColumns);
      console.log('Export payload:', payload);
      
      const response = await api.post('/lms/leads/v2/', payload);
      console.log('Export response:', response.data);
      
      if (response.data && response.data.success) {
        const count = response.data.count ?? 0;
        const message =
          response.data.message ||
          'Your export request was received. You will receive an email with the CSV file shortly.';
        const detail =
          count > 0
            ? ` This export includes ${count} lead${count !== 1 ? 's' : ''}.`
            : '';
        openNoChangesModal('Export requested', `${message}${detail} Please check your email inbox.`);
      } else {
        console.error('Export failed: Invalid response', response.data);
        openErrorModal('Export failed', 'Invalid response from server. Please try again.');
      }
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Unknown error';
      openErrorModal('Export failed', errorMessage);
      setError('Export failed');
    }
  }, [dateRangeState, visibleColumns, openNoChangesModal, openErrorModal]);
  
  return {
    // State
    state,
    leads,
    isLoading,
    error,
    paginationInfo,
    
    // Actions
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFiltersChange,
    handleRefresh,
    handleExport,
    updateState,
    
    // Utilities
    isCustomFetchMode: !!state.statType,
  };
};

// Helper functions
function parseFiltersFromURL(searchParams) {
  const filters = {};
  
  // Parse array filters - include all possible filter types
  const arrayFilters = [
    'lead_stage', 
    'status', 
    'tags', 
    'agents', 
    'agent_id',
    'teams',
    'source', 
    'bhk_type', 
    'lead_urgency', 
    'lead_lcp', 
    'occupation',
    'site_visit_frequency',
    'budget_ranges',
    'locations',
    'property_types',
    'intent',
  ];
  arrayFilters.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      filters[key] = value.split(',');
    }
  });
  
  // Parse budget filter
  const budgetMin = searchParams.get('budget_min');
  const budgetMax = searchParams.get('budget_max');
  if (budgetMin || budgetMax) {
    filters.budget = {
      min_value: budgetMin ? parseInt(budgetMin) : undefined,
      max_value: budgetMax ? parseInt(budgetMax) : undefined,
    };
  }
  
  // Parse last_contacted filter
  const lastContactedPreset = searchParams.get('last_contacted_preset');
  if (lastContactedPreset) {
    filters.last_contacted = {
      preset: lastContactedPreset,
      start_time: searchParams.get('last_contacted_start'),
      end_time: searchParams.get('last_contacted_end'),
    };
  }
  
  // Parse unassigned filter
  const unassigned = searchParams.get('unassigned');
  if (unassigned === 'true') {
    filters.unassigned = true;
  }
  
  // Parse not_contacted filter
  const notContacted = searchParams.get('last_contacted_at__isnull');
  if (notContacted === 'true') {
    filters.last_contacted_at__isnull = true;
  }
  
  return filters;
}

function addFiltersToURL(params, filters) {
  if (!filters) return;
  
  // Add array filters
  Object.keys(filters).forEach(key => {
    const value = filters[key];
    
    if (Array.isArray(value) && value.length > 0) {
      // Convert objects to values if needed
      const stringValues = value.map(item => {
        if (typeof item === 'object' && item !== null && item.value !== undefined) {
          return item.value;
        }
        return item;
      });
      params.set(key, stringValues.join(','));
    } else if (key === 'budget' && value) {
      if (value.min_value) params.set('budget_min', value.min_value);
      if (value.max_value) params.set('budget_max', value.max_value);
    } else if (key === 'last_contacted' && value) {
      if (value.preset) params.set('last_contacted_preset', value.preset);
      if (value.start_time) params.set('last_contacted_start', value.start_time);
      if (value.end_time) params.set('last_contacted_end', value.end_time);
    } else if (key === 'unassigned' && value === true) {
      params.set('unassigned', 'true');
    } else if (key === 'last_contacted_at__isnull' && value === true) {
      params.set('last_contacted_at__isnull', 'true');
    }
  });
}

function buildAPIPayload(state, dateRangeState = null, visibleColumns = []) {
  const payload = {
    page: state.page,
    page_size: state.pageSize,
    sort_field: state.sortField,
    sort_order: state.sortOrder,
    is_deleted: state.isDeleted,
  };
  
  // Add visible columns if provided
  if (visibleColumns && visibleColumns.length > 0) {
    payload.visible_columns = visibleColumns;
  }
  
  
  // Add date range for standard mode
  if (!state.statType && dateRangeState) {
    // Check if date range is set and not "All"
    if (dateRangeState.filter?.id !== 'All' && dateRangeState.currentStart && dateRangeState.currentEnd) {
      payload.date_range = {
        start_date: dateRangeState.currentStart.format('YYYY-MM-DD'),
        end_date: dateRangeState.currentEnd.format('YYYY-MM-DD')
      };
    }
    
    // Handle all_results flag
    if (dateRangeState.all_results !== undefined) {
      payload.all_results = dateRangeState.all_results;
    } else if (dateRangeState.filter?.id === 'All') {
      payload.all_results = true;
    }
  }
  
  // Custom fetch mode
  if (state.statType) {
    payload.stat_type = state.statType;
    
    if (state.startDate && state.endDate) {
      payload.date_range = {
        start_date: state.startDate,
        end_date: state.endDate,
      };
    }
    
    if (state.assignedTo && state.assignedTo.length > 0) {
      payload.assigned_to = state.assignedTo;
    }
    
    // Add not_interested_reason_category if provided
    if (state.notInterestedReasonCategory) {
      payload.not_interested_reason_category = decodeURIComponent(state.notInterestedReasonCategory);
    }
  }
  
  // Merge pipelineFilters (stage/status from sidebar) with user filters — AND logic
  const mergedFilters = { ...(state.pipelineFilters || {}), ...(state.filters || {}) };

  // Standard filters - process each filter properly
  if (mergedFilters) {
    Object.keys(mergedFilters).forEach(key => {
      const value = mergedFilters[key];
      
      // Skip null/undefined values
      if (value === null || value === undefined) {
        return;
      }
      
      // Handle array filters - convert objects to values
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return; // Skip empty arrays
        }
        
        // Convert objects to values for backend
        const processedValues = value.map(item => {
          if (typeof item === 'object' && item !== null && item.value !== undefined) {
            return item.value;
          }
          return item;
        });
        
        // Handle agent_id -> agents conversion for backend
        if (key === 'agent_id') {
          payload.agents = processedValues;
        } else if (key === 'teams') {
          // Backend dashboard drill-down expects team_filter (list of IDs)
          const teamIds = processedValues.map(v => (typeof v === 'number' ? v : parseInt(v, 10))).filter(n => !isNaN(n));
          if (teamIds.length > 0) {
            payload.team_filter = teamIds;
          }
        } else {
          payload[key] = processedValues;
        }
      }
      // Handle budget filter
      else if (key === 'budget' && typeof value === 'object') {
        // Ensure budget is an object, not an array
        if (Array.isArray(value) && value.length > 0) {
          payload.budget = value[0];
        } else {
          payload.budget = value;
        }
      }
      // Handle last_contacted filter
      else if (key === 'last_contacted' && typeof value === 'object') {
        // Store under filters for bookmarks AND hoist to top-level
        // so /lms/leads/v2/ can apply it via _apply_last_contacted_filter.
        payload.last_contacted = value;
      }
      // Handle boolean filters (unassigned, last_contacted_at__isnull, etc.)
      else if (typeof value === 'boolean') {
        payload[key] = value;
      }
      // Handle other filters
      else if (value !== '' && value !== false) {
        payload[key] = value;
      }
    });
  }
  
  // Normalize lead_stage in payload for pipeline behavior:
  // When qualified is selected, also include follow_up (and vice versa)
  if (payload.lead_stage && Array.isArray(payload.lead_stage)) {
    let stages = [...payload.lead_stage];
    const hasQualified = stages.includes('qualified');
    const hasFollowUp = stages.includes('follow_up');
    if (hasQualified && !hasFollowUp) stages.push('follow_up');
    if (hasFollowUp && !hasQualified) stages.push('qualified');
    payload.lead_stage = Array.from(new Set(stages));
  }
  
  // Export: CSV columns are exactly `visible_columns` from the UI (no default column list)
  if (state.is_export) {
    payload.is_export = true;
    payload.visible_columns = Array.isArray(visibleColumns) ? [...visibleColumns] : [];
  }

  return payload;
}

function downloadCSV(data) {
  try {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }
    
    console.log('Converting to CSV, data length:', data.length);
    
    // Get all unique headers from all rows
    const allHeaders = new Set();
    data.forEach(row => {
      Object.keys(row).forEach(key => allHeaders.add(key));
    });
    const headers = Array.from(allHeaders);
    
    // Escape CSV values properly
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If contains comma, newline, or quote, wrap in quotes and escape quotes
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    
    // Convert to CSV
    const csvRows = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle nested objects/arrays
          if (typeof value === 'object' && value !== null) {
            return escapeCSV(JSON.stringify(value));
          }
          return escapeCSV(value);
        }).join(',')
      )
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Add BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    
    console.log('CSV download initiated');
  } catch (error) {
    console.error('Error in downloadCSV:', error);
    alert(`Error generating CSV: ${error.message}`);
  }
}

export default useLeadsV2;

